import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ProcessJobType } from '@/graphql/graphql';
import { useDialog } from '@/providers/DialogProvider';

import type { CompareResult } from './compare/availability';
import { openCompareSurfacesRerun } from './rerunOpeners';
import { openLandxmlMerge } from './openAnalysisDialogs';
import {
  buildHalfRerunPlan,
  buildPairedRerunPlan,
  buildPartRerunPlan,
  buildRerunPlan,
  type CompareRerunPlan,
} from './rerun';
import { fetchAnalysisRunInput } from './runInput';
import type { AnalysisId } from './types';
import { useAnalysisAvailability } from './useAnalysisAvailability';
import { useAnalysisProjectContext } from './useAnalysisProjectContext';
import type { LaunchOptions } from './useLaunchAnalysis';

/**
 * Re-run of a past analysis (D12). Fetches the run's input lazily (one row,
 * never in the polled list), maps it with `buildRerunPlan`, and
 *  - opens Compare surfaces itself, with the run's values restored — `jobType`
 *    (the hub row's) says which engine the run came from;
 *  - returns launch options for every other type — `useLaunchAnalysis` then
 *    opens that dialog through its normal path with the run's source as the
 *    context layer (those dialogs have no initial-values prop);
 *  - returns null when it handled (or refused) the re-run.
 */
export function useOpenAnalysisRerun(): (
  id: AnalysisId,
  jobId: string,
  jobType?: ProcessJobType,
) => Promise<Omit<LaunchOptions, 'rerunJobId' | 'rerunJobType'> | null> {
  const { t } = useTranslation('project');
  const queryClient = useQueryClient();
  const { openDialog } = useDialog();
  const availability = useAnalysisAvailability();
  const ctx = useAnalysisProjectContext();

  return async (id, jobId, jobType) => {
    const gate = availability(id);
    if (!gate.visible) return null;
    if (gate.locked) {
      gate.promptUpgrade();
      return null;
    }
    if (!ctx) return null;

    const input = await fetchAnalysisRunInput(queryClient, jobId);
    if (input == null) {
      toast.error(t('analysisLauncher.runs.inputFailed'));
      return null;
    }

    const plan = buildRerunPlan(id, input, ctx.layers, jobType);
    if (!plan) return {};
    // A LandXML export's only input is its model: that case gets the more
    // specific `sourceGone` error below instead of this hint.
    if (plan.missingInputs && plan.id !== 'landxmlExport') toast.info(t('analysisLauncher.runs.inputsMissing'));

    const env = { openDialog, projectId: ctx.projectId, layers: ctx.layers };
    switch (plan.id) {
      case 'compare':
        openCompareSurfacesRerun(env, plan);
        return null;
      case 'landxmlExport':
        if (!plan.sourceLayer) {
          toast.error(t('analysisLauncher.runs.sourceGone'));
          return null;
        }
        return { contextLayer: plan.sourceLayer };
      case 'landxmlMerge':
        openLandxmlMerge(env, undefined, {
          sourceLayerIds: plan.sourceLayerIds,
          outputName: plan.outputName,
          missingInputs: plan.missingInputs,
        });
        return null;
      default:
        return plan.sourceLayer ? { contextLayer: plan.sourceLayer } : {};
    }
  };
}

/** The two jobs of a paired comparison (the hub's merged row). */
export interface ComparisonRerunJobs {
  cutFillJobId: string;
  deviationJobId: string;
}

/**
 * Re-run of a paired comparison from the hub (spec §12.9): "both" ticks both
 * results, a part re-run only that one; either way the form takes the values
 * of both halves (`buildPairedRerunPlan` / `buildPartRerunPlan`). When one
 * half cannot be read — its part failed to start, so no job holds its input —
 * any re-run (both included) is seeded from the readable half
 * (`buildHalfRerunPlan`). Only when neither can be read does it refuse. Same entry gate as `useOpenAnalysisRerun`; the deviation card's own
 * gate is the caller's (`comparisonRerunChoices` locks those choices).
 */
export function useOpenComparisonRerun(): (jobs: ComparisonRerunJobs, results: CompareResult[]) => Promise<void> {
  const { t } = useTranslation('project');
  const queryClient = useQueryClient();
  const { openDialog } = useDialog();
  const availability = useAnalysisAvailability();
  const ctx = useAnalysisProjectContext();

  return async ({ cutFillJobId, deviationJobId }, results) => {
    const gate = availability('compare');
    if (!gate.visible || results.length === 0) return;
    if (gate.locked) {
      gate.promptUpgrade();
      return;
    }
    if (!ctx) return;

    const [sdInput, devInput] = await Promise.all([
      fetchAnalysisRunInput(queryClient, cutFillJobId),
      fetchAnalysisRunInput(queryClient, deviationJobId),
    ]);
    let plan: CompareRerunPlan | null = null;
    if (sdInput != null && devInput != null) {
      plan =
        results.length > 1
          ? buildPairedRerunPlan(sdInput, devInput, ctx.layers)
          : buildPartRerunPlan(results[0], sdInput, devInput, ctx.layers);
    } else if (sdInput != null) {
      plan = buildHalfRerunPlan('cutFill', sdInput, ctx.layers, results);
    } else if (devInput != null) {
      plan = buildHalfRerunPlan('deviation', devInput, ctx.layers, results);
    }
    if (!plan) {
      toast.error(t('analysisLauncher.runs.inputFailed'));
      return;
    }
    if (plan.missingInputs) toast.info(t('analysisLauncher.runs.inputsMissing'));
    openCompareSurfacesRerun({ openDialog, projectId: ctx.projectId, layers: ctx.layers }, plan);
  };
}
